<script setup lang="ts">
import { useQueryClient } from "@tanstack/vue-query";
import debounce from "lodash-es/debounce";

import { queries } from "~/queries";
import { useInfiniteProductsList } from "~/queries/catalog/products";

type Props = {
  currentProductId?: string;
};

const props = defineProps<Props>();
const currentProductId = computed(() => props.currentProductId);
const recentProducts = useRecentProducts(currentProductId);

const hasRecentProducts = computed(() => recentProducts.value.length > 0);
const productQueryFilters = computed(() => ({
  productIds: recentProducts.value,
}));

const {
  data: products,
  isLoading,
  refetch: refetchRecentlyViewedProducts,
} = useInfiniteProductsList(
  { filters: productQueryFilters, pageSize: 20 },
  {
    enabled: hasRecentProducts,
    select: (products) => {
      const productsSort = recentProducts.value;

      // Sort the products from the API in the order that we saved them in locally.
      // That way the most recently viewed products always show up on top
      return products.sort(
        (a, b) => productsSort.indexOf(a.id) - productsSort.indexOf(b.id),
      );
    },
  },
);
// debounce in case multiple products reach their endDate at the same time
const debounceRefetchRecentlyViewedProducts = debounce(
  refetchRecentlyViewedProducts,
  1000,
);

const { addRecentlyViewedProductClick } = useAnalytics();
const queryClient = useQueryClient();
const platformStore = usePlatformStore();
const platformId = computed(
  () => platformStore.getPlatformConfiguration.platformId,
);

const onClickRecentlyViewedProduct = async (productId: string) => {
  const product = products.value?.data?.find(
    (product) => product.id === productId,
  );

  if (product?.eventId) {
    const event = await queryClient.fetchQuery(
      queries.events.details(product.eventId, platformId),
    );

    addRecentlyViewedProductClick({
      productPrices: product.prices,
      productId: product.id,
      productName: product.name,
      productSlug: product.slug,
      labels: product.labels.map((label) => label.text),
      event,
    });
  }
};
</script>

<template>
  <div
    v-if="hasRecentProducts"
    class="col-span-12 mt-8 mb-4 md:col-span-10 md:col-start-2"
  >
    <h3 class="text-xl uppercase font-bold px-4 mb-4 md:px-0">
      {{ $t("product.recentlyViewed") }}
    </h3>
    <ProductSectionProductsGrid
      class="products-grid-wrapper"
      :products="products?.data ?? []"
      :show-skeleton="isLoading"
      :has-margin="false"
      tracking-source="recentlyViewedProducts"
      @product-clicked="(product) => onClickRecentlyViewedProduct(product.id)"
      @product-finished="debounceRefetchRecentlyViewedProducts"
    />
    <ProductSectionProductsSlider
      class="md:hidden"
      :products="products?.data"
      :is-loading="isLoading"
      :has-padding="false"
      tracking-source="recentlyViewedProducts"
      @product-clicked="(product) => onClickRecentlyViewedProduct(product.id)"
      @product-finished="debounceRefetchRecentlyViewedProducts"
    />
  </div>
</template>

<style scoped lang="postcss">
.products-grid-wrapper {
  @apply hidden md:grid;
}
</style>
